import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { type GestureResponderEvent, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Modal } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { useAccountTags } from '@/hooks/use-account-tags';
import { ACCOUNT_FLAG_COLORS, ACCOUNT_FLAG_DESCRIPTIONS, ACCOUNT_FLAG_LABELS, type AccountFlag, type AccountTag } from '@/types/models';

const DEFAULT_BADGE_COLOR = '#37474f';

/** Resolve a flag code → display props from the DB registry, falling back to
 *  the built-in constants and finally the raw code. */
const makeResolver = (byCode: Map<string, AccountTag>) => ({
	color: (f: string) => byCode.get(f)?.color ?? ACCOUNT_FLAG_COLORS[f as AccountFlag] ?? DEFAULT_BADGE_COLOR,
	label: (f: string) => byCode.get(f)?.label ?? ACCOUNT_FLAG_LABELS[f as AccountFlag] ?? f,
	desc: (f: string) => byCode.get(f)?.description ?? ACCOUNT_FLAG_DESCRIPTIONS[f as AccountFlag] ?? '',
});

interface Props {
	flags: AccountFlag[] | undefined;
	size?: 'compact' | 'normal';
}

const isWeb = Platform.OS === 'web';

/**
 * Inline pill list of account flags. On web each pill carries a native
 * `title` tooltip (slow but eventually shows the label+description) AND is
 * tappable to open a small modal listing every flag with full descriptions.
 * Tap stops event propagation so a parent row Pressable (e.g. account-list
 * row that navigates on press) doesn't also fire.
 */
export const AccountFlagBadges: React.FC<Props> = ({ flags, size = 'normal' }) => {
	const [open, setOpen] = useState(false);
	const { data: tags } = useAccountTags();
	const resolve = useMemo(() => makeResolver(new Map((tags ?? []).map((t) => [t.code, t]))), [tags]);
	if (!flags || flags.length === 0) return null;
	const compact = size === 'compact';

	const handlePress = (e: GestureResponderEvent) => {
		// Stop the outer row Pressable from also receiving the click (which would
		// navigate to the account). React SyntheticEvent's stopPropagation halts
		// bubbling to ancestor React handlers; calling the native one too is a
		// belt-and-suspenders guard for RN-Web's click-delegation quirks.
		e?.stopPropagation?.();
		(e as unknown as { nativeEvent?: { stopPropagation?: () => void } })?.nativeEvent?.stopPropagation?.();
		setOpen(true);
	};

	return (
		<>
			<View style={styles.row}>
				{flags.map((f) => {
					const label = resolve.label(f);
					const desc = resolve.desc(f);
					const tooltip = desc ? `${label} — ${desc}` : label;
					const pressable = (
						<Pressable
							key={f}
							onPress={handlePress}
							accessibilityRole="button"
							accessibilityLabel={`${label} — tap for details`}
							style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
								styles.badge,
								{ backgroundColor: resolve.color(f) },
								compact && styles.badgeCompact,
								hovered && styles.badgeHovered,
								pressed && styles.badgePressed,
							]}
						>
							<Text style={[styles.label, compact && styles.labelCompact]}>{f}</Text>
						</Pressable>
					);
					// Wrap in a real DOM `<div title>` on web so the native browser
					// tooltip eventually surfaces on long hover. RN-Web's <View>
					// doesn't forward `title`, hence the createElement escape hatch.
					return isWeb
						? React.createElement(
							'div',
							{ key: f, title: tooltip, style: { display: 'inline-flex' } },
							pressable,
						)
						: pressable;
				})}
			</View>
			<Modal
				visible={open}
				onDismiss={() => setOpen(false)}
				contentContainerStyle={styles.modal}
			>
				<View style={styles.modalHeader}>
					<Text style={styles.modalTitle}>
						{flags.length === 1 ? resolve.label(flags[0]) : 'Account flags'}
					</Text>
					<Pressable
						onPress={() => setOpen(false)}
						hitSlop={8}
						accessibilityRole="button"
						accessibilityLabel="Close"
					>
						<Ionicons name="close" size={22} color="#455a64" />
					</Pressable>
				</View>
				<ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
					{flags.map((f) => (
						<View key={f} style={styles.flagRow}>
							<View style={[styles.flagBadgeInline, { backgroundColor: resolve.color(f) }]}>
								<Text style={styles.flagBadgeInlineLabel}>{f}</Text>
							</View>
							<View style={styles.flagText}>
								{flags.length > 1 && (
									<Text style={styles.flagLabel}>{resolve.label(f)}</Text>
								)}
								<Text style={styles.flagDescription}>
									{resolve.desc(f) || 'No description.'}
								</Text>
							</View>
						</View>
					))}
				</ScrollView>
			</Modal>
		</>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
	badge: {
		paddingVertical: 2,
		paddingHorizontal: 6,
		borderRadius: 4,
		backgroundColor: '#37474f',
	},
	badgeCompact: { paddingVertical: 1, paddingHorizontal: 4, borderRadius: 3 },
	// Keep each badge's base color on hover/press — preserve severity color
	// (red/amber/blue) and signal interactivity with opacity only.
	badgeHovered: { opacity: 0.85 },
	badgePressed: { opacity: 0.65 },
	label: { color: '#fff', fontSize: 10, fontWeight: '700' },
	labelCompact: { fontSize: 9 },

	modal: { width: 420, maxWidth: '95%' },
	modalHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 12,
	},
	modalTitle: { fontSize: 18, fontWeight: '700', color: '#0d47a1' },
	modalScroll: { maxHeight: 420 },
	modalScrollContent: { gap: 14, paddingBottom: 4 },

	flagRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
	flagBadgeInline: {
		paddingVertical: 3,
		paddingHorizontal: 8,
		borderRadius: 4,
		backgroundColor: '#37474f',
		minWidth: 56,
		alignItems: 'center',
	},
	flagBadgeInlineLabel: { color: '#fff', fontSize: 11, fontWeight: '700' },
	flagText: { flex: 1, minWidth: 0, gap: 2 },
	flagLabel: { fontSize: 14, fontWeight: '700', color: '#263238' },
	flagDescription: { fontSize: 13, color: '#546e7a', lineHeight: 18 },
});
